import React, { useCallback, useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Search, Route, AlertTriangle, Home, Banknote, SlidersHorizontal, Users, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

const gbp = (n) => `£${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
// "3 weeks in Sourcing" reads better than "21 days".
const months = (days) => (days < 45 ? `${Math.max(1, Math.round(days / 7))}w` : `${Math.round(days / 30)}mo`);
const FLAG_BAR = { red: "bg-brand-red", amber: "bg-amber-400", ok: "bg-brand-green/60" };
const mins = (m) => (m >= 60 ? `${(m / 60).toFixed(1)}h` : `${m || 0}m`);
const lastSeen = (iso) => {
  if (!iso) return tr("never");
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? tr("today") : d === 1 ? tr("yesterday") : `${d}${tr("d ago")}`;
};

export default function Journey() {
  const { user, isSuper } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [manager, setManager] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [card, setCard] = useState(null); // student being edited
  const [form, setForm] = useState({ units: 0, monthly_revenue_gbp: 0, note: "" });
  const [slas, setSlas] = useState({});

  const [usage, setUsage] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await client.get("/journey");
      setData(r.data);
      setSlas(Object.fromEntries(r.data.stages.map((s) => [s.id, { amber: s.amber, red: s.red }])));
    } catch (e) { toast.error(tr("Couldn't load the board"), { description: e?.response?.data?.detail }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // How much each student is actually using the app (summary only — /activity has the detail).
  const loadUsage = useCallback(async () => {
    try {
      const r = await client.get("/activity/students");
      setUsage(Object.fromEntries((r.data.students || []).map((s) => [s.user_id, s])));
    } catch (e) { console.error("Activity summary failed", e); }
  }, []);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const managerName = (id) => data?.managers?.find((m) => m.user_id === id)?.name || "Unassigned";
  // Computed client-side so the header reacts the moment you edit a card.
  const totals = useMemo(() => {
    const list = data?.students || [];
    return {
      students: list.length,
      units: list.reduce((s, x) => s + (x.units || 0), 0),
      revenue: list.reduce((s, x) => s + (x.monthly_revenue_gbp || 0), 0),
      flagged: list.filter((x) => x.flag === "red").length,
    };
  }, [data]);

  const shown = useMemo(() => {
    const list = data?.students || [];
    const needle = q.trim().toLowerCase();
    return list.filter((s) => {
      const mids = [s.assigned_admin_id, ...(s.support_admin_ids || [])].filter(Boolean);
      if (manager === "none" ? mids.length > 0 : manager !== "all" && !mids.includes(manager)) return false;
      if (!needle) return true;
      return `${s.name} ${s.email} ${s.agency}`.toLowerCase().includes(needle);
    });
  }, [data, q, manager]);

  const patch = async (uid, body) => {
    setData((d) => ({ ...d, students: d.students.map((s) => (s.user_id === uid ? { ...s, ...body } : s)) }));
    try {
      const r = await client.patch(`/journey/${uid}`, body);
      setData((d) => ({ ...d, students: d.students.map((s) => (s.user_id === uid ? r.data : s)) }));
    } catch (e) { toast.error(tr("Couldn't save"), { description: e?.response?.data?.detail }); load(); }
  };

  const drop = async (stageId) => {
    setOverStage(null);
    const s = (data?.students || []).find((x) => x.user_id === dragId);
    setDragId(null);
    if (!s || s.stage === stageId) return;
    await patch(s.user_id, { stage: stageId });
    toast.success(`${s.name} moved to ${data.stages.find((x) => x.id === stageId)?.label}`);
  };

  const openCard = (s) => {
    setCard(s);
    setForm({ units: s.units, monthly_revenue_gbp: s.monthly_revenue_gbp, note: s.note || "" });
  };
  const saveCard = async () => {
    await patch(card.user_id, {
      units: Number(form.units) || 0,
      monthly_revenue_gbp: Number(form.monthly_revenue_gbp) || 0,
      note: form.note,
    });
    setCard(null);
    toast.success(tr("Saved"));
  };

  const saveSlas = async () => {
    // A red limit below amber would never let a card go amber first.
    const clean = Object.fromEntries(Object.entries(slas).map(([k, v]) => {
      const amber = Math.max(0, Number(v.amber) || 0);
      const red = Math.max(0, Number(v.red) || 0);
      return [k, { amber, red: red && amber ? Math.max(red, amber) : red }];
    }));
    try {
      const r = await client.put("/journey/slas", { slas: clean });
      setSlas(Object.fromEntries(r.data.stages.map((s) => [s.id, { amber: s.amber, red: s.red }])));
      setData((d) => ({ ...d, stages: r.data.stages }));
      toast.success(tr("Stage limits saved"));
      load();
    } catch (e) { toast.error(tr("Couldn't save limits"), { description: e?.response?.data?.detail }); }
  };

  const Stat = ({ icon: Icon, label, value, tone }) => (
    <div className={`rounded-xl border px-3 py-2 ${tone === "red" ? "border-brand-red/25 bg-brand-red/[0.06]" : "border-white/[0.08] bg-white/[0.02]"}`}>
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"><Icon className="h-3 w-3" /> {label}</p>
      <p className={`font-display text-lg font-semibold ${tone === "red" ? "text-brand-red" : "text-white"}`}>{value}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-white/[0.06] bg-background/80 px-4 py-3 backdrop-blur-xl sm:px-6">
        <button onClick={() => window.location.assign("/admin")} data-testid="journey-back-btn"
          className="inline-flex items-center gap-1 rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-sm text-white hover:bg-white/[0.07]"><ArrowLeft className="h-4 w-4" /> {tr("Console")}</button>
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-green/15 text-brand-green"><Route className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h1 className="font-display text-lg font-semibold text-white">{tr("Student Journey")}</h1>
          <p className="truncate text-xs text-muted-foreground">{shown.length} / {totals.students} {tr("students across")} {data?.stages?.length ?? 0} {tr("stages")}</p>
        </div>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <Stat icon={Users} label={tr("Students")} value={totals.students} />
          <Stat icon={Home} label={tr("Units")} value={totals.units} />
          <Stat icon={Banknote} label={tr("Monthly")} value={gbp(totals.revenue)} />
          <Stat icon={AlertTriangle} label={tr("Stuck")} value={totals.flagged} tone="red" />

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Name, email…")} data-testid="journey-search"
              className="h-[38px] w-[200px] border-white/12 bg-white/[0.03] pl-9 text-sm" />
          </div>
          <button onClick={() => setManager(manager === user?.user_id ? "all" : user?.user_id)}
            data-testid="journey-my-students"
            className={`h-[38px] rounded-lg border px-3 text-xs font-semibold transition-colors ${
              manager === user?.user_id
                ? "border-brand-green/35 bg-brand-green/10 text-brand-green"
                : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
            {tr("My students")}
          </button>
          <Select value={manager} onValueChange={setManager}>
            <SelectTrigger className="h-[38px] w-[230px] border-white/12 bg-white/[0.03] text-sm" data-testid="journey-manager-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="glass-strong border-white/10">
              <SelectItem value="all">{tr("All success managers")}</SelectItem>
              <SelectItem value="none">{tr("Unassigned")}</SelectItem>
              {(data?.managers || []).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {isSuper && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="grid h-[38px] w-[38px] place-items-center rounded-lg border border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white" data-testid="stage-limits-btn" title={tr("Stage limits")}>
                  <SlidersHorizontal className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 glass-strong border-white/10">
                <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Stuck warnings (days in stage)")}</p>
                <p className="mb-3 text-xs text-muted-foreground">{tr("Amber warns you, red flags them as stuck. 0 = no limit.")}</p>
                <div className="max-h-[320px] space-y-2 overflow-auto pr-1">
                  {(data?.stages || []).map((s) => (
                    <div key={s.id} className="flex items-center gap-2">
                      <span className="flex-1 truncate text-xs text-white">{s.label}</span>
                      <Input type="number" min="0" value={slas[s.id]?.amber ?? 0} data-testid={`sla-amber-${s.id}`}
                        onChange={(e) => setSlas((p) => ({ ...p, [s.id]: { ...p[s.id], amber: Number(e.target.value) } }))}
                        className="h-8 w-16 border-amber-400/30 bg-input/40 text-center text-xs" />
                      <Input type="number" min="0" value={slas[s.id]?.red ?? 0} data-testid={`sla-red-${s.id}`}
                        onChange={(e) => setSlas((p) => ({ ...p, [s.id]: { ...p[s.id], red: Number(e.target.value) } }))}
                        className="h-8 w-16 border-brand-red/30 bg-input/40 text-center text-xs" />
                    </div>
                  ))}
                </div>
                <Button onClick={saveSlas} data-testid="save-slas-btn" className="mt-3 h-9 w-full bg-brand-green hover:bg-brand-green/90 text-white">{tr("Save limits")}</Button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </header>

      <main className="overflow-x-auto px-4 py-6 sm:px-6">
        <div className="flex min-h-[70vh] gap-4">
          {(data?.stages || []).map((stage) => {
            const cards = shown.filter((s) => s.stage === stage.id);
            return (
              <div key={stage.id} data-testid={`journey-col-${stage.id}`}
                onDragOver={(e) => { e.preventDefault(); setOverStage(stage.id); }}
                onDragLeave={() => setOverStage((p) => (p === stage.id ? null : p))}
                onDrop={() => drop(stage.id)}
                className={`w-[288px] shrink-0 rounded-2xl border p-3 transition-colors ${overStage === stage.id ? "border-brand-green/50 bg-brand-green/[0.05]" : "border-white/[0.06] bg-card/40"}`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="truncate text-sm font-medium text-white">{stage.label}</h2>
                  <span className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[11px] text-muted-foreground" data-testid={`journey-count-${stage.id}`}>{cards.length}</span>
                </div>
                <div className="space-y-2.5">
                  {cards.map((s) => (
                    <div key={s.user_id} draggable data-testid="journey-card"
                      onDragStart={() => setDragId(s.user_id)} onDragEnd={() => setDragId(null)}
                      onClick={() => openCard(s)}
                      className={`group relative cursor-pointer overflow-hidden rounded-xl border border-white/[0.07] bg-background/60 p-3 pl-4 transition-colors hover:border-white/20 ${dragId === s.user_id ? "opacity-40" : ""}`}>
                      <span className={`absolute left-0 top-0 h-full w-1 ${FLAG_BAR[s.flag]}`} />
                      <div className="flex items-start gap-2">
                        <img src={s.avatar} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-white/10" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-white">{s.name}</p>
                          <p className="truncate text-[11px] text-muted-foreground">{s.email}</p>
                        </div>
                        <span className="shrink-0 rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground" data-testid="card-manager-badge">
                          {managerName(s.assigned_admin_id).split(" ")[0]}
                          {(s.support_admin_ids || []).length > 0 && ` +${s.support_admin_ids.length}`}
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="journey-timing">
                        {months(s.days_in_programme)} {tr("in programme")} ·{" "}
                        <span className={s.flag === "red" ? "text-brand-red" : s.flag === "amber" ? "text-amber-300" : "text-white/70"}>
                          {s.days_in_stage}d here
                        </span>
                      </p>
                      {(s.units > 0 || s.monthly_revenue_gbp > 0) && (
                        <div className="mt-2 flex items-center gap-1.5">
                          {s.units > 0 && <span className="rounded-md border border-brand-green/25 bg-brand-green/10 px-1.5 py-0.5 text-[10px] text-brand-green" data-testid="journey-units">{s.units} unit{s.units === 1 ? "" : "s"}</span>}
                          {s.monthly_revenue_gbp > 0 && <span className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-white/80" data-testid="journey-revenue">{gbp(s.monthly_revenue_gbp)}/mo</span>}
                        </div>
                      )}
                      {usage[s.user_id] && (
                        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="journey-usage">
                          <Clock className="h-3 w-3" />
                          {usage[s.user_id].stale
                            ? <span className="text-brand-red">{tr("quiet since")} {lastSeen(usage[s.user_id].last_login)}</span>
                            : <span>seen {lastSeen(usage[s.user_id].last_login)} · {mins(usage[s.user_id].minutes_month)} this month</span>}
                        </p>
                      )}
                      {s.note && <p className="mt-2 line-clamp-2 text-[11px] italic text-muted-foreground/80">{s.note}</p>}
                    </div>
                  ))}
                  {cards.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground/60">{tr("Drop a student here")}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <Dialog open={!!card} onOpenChange={(o) => { if (!o) setCard(null); }}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-white">{card?.name}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {card && <>{data?.stages?.find((s) => s.id === card.stage)?.label} · {card.days_in_stage} days in this stage · {months(card.days_in_programme)} in the programme · {[card.assigned_admin_id, ...(card.support_admin_ids || [])].filter(Boolean).map(managerName).join(", ") || "no manager assigned"}</>}
              {card && usage[card.user_id] && (
                <span className="mt-1 block" data-testid="card-usage">
                  Last signed in {lastSeen(usage[card.user_id].last_login)} · {mins(usage[card.user_id].minutes_month)} in the app this month
                  {" · "}
                  <button onClick={() => window.location.assign("/activity")} className="text-brand-red hover:underline">{tr("see activity")}</button>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">{tr("Units live")}</label>
                <Input type="number" min="0" value={form.units} data-testid="card-units-input"
                  onChange={(e) => setForm((f) => ({ ...f, units: e.target.value }))}
                  className="h-10 border-white/8 bg-input/40" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">{tr("Monthly revenue (£)")}</label>
                <Input type="number" min="0" value={form.monthly_revenue_gbp} data-testid="card-revenue-input"
                  onChange={(e) => setForm((f) => ({ ...f, monthly_revenue_gbp: e.target.value }))}
                  className="h-10 border-white/8 bg-input/40" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{tr("Stage")}</label>
              <Select value={card?.stage || ""} onValueChange={(v) => { patch(card.user_id, { stage: v }); setCard((c) => ({ ...c, stage: v })); }}>
                <SelectTrigger className="h-10 border-white/8 bg-input/40" data-testid="card-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {(data?.stages || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{tr("What's happening / blockers")}</label>
              <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="card-note-input"
                placeholder={tr("e.g. waiting on bank verification since the 3rd")} className="min-h-[90px] border-white/8 bg-input/40 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => setCard(null)} className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
            <Button onClick={saveCard} data-testid="card-save-btn" className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white">{tr("Save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
